import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePortalAuth, portalApi } from "../../context/PortalAuthContext";
import { ShoppingCart, Package, Plus, Minus, LogOut, Clock, ChevronRight, Truck, MapPin, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatMXN } from "../../utils/format";

function CountdownBadge({ expiresAt }) {
  const [timeLeft, setTimeLeft] = useState("");
  const [urgent, setUrgent] = useState(false);

  useEffect(() => {
    const tick = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setTimeLeft("Expirado"); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
      setUrgent(diff < 3600000);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${urgent ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
      <Clock size={11} /> {timeLeft}
    </span>
  );
}

export default function PortalTienda() {
  const { customer, logout } = usePortalAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [orders, setOrders] = useState([]);
  const [states, setStates] = useState([]);
  const [tab, setTab] = useState("catalogo");
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  
  // Shipping state
  const [shippingState, setShippingState] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingPostal, setShippingPostal] = useState("");
  const [shippingQuote, setShippingQuote] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, oRes, sRes] = await Promise.all([
        portalApi.get("/products"),
        portalApi.get("/my-orders"),
        portalApi.get("/states"),
      ]);
      setProducts(pRes.data);
      setOrders(oRes.data);
      setStates(sRes.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); navigate("/tienda/login"); }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const qty = (id) => cart[id]?.qty || 0;
  const addItem = (p) => setCart(c => ({ ...c, [p.id]: { product: p, qty: (c[p.id]?.qty || 0) + 1 } }));
  const removeItem = (p) => setCart(c => {
    const q = (c[p.id]?.qty || 0) - 1;
    if (q <= 0) { const n = { ...c }; delete n[p.id]; return n; }
    return { ...c, [p.id]: { product: p, qty: q } };
  });
  const setQty = (p, v) => {
    const n = parseInt(v) || 0;
    if (n <= 0) { const c2 = { ...cart }; delete c2[p.id]; setCart(c2); }
    else setCart(c => ({ ...c, [p.id]: { product: p, qty: n } }));
  };

  const cartItems = Object.values(cart);
  const cartSubtotal = cartItems.reduce((s, i) => s + i.product.portal_price * i.qty, 0);
  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);
  const totalWeight = cartItems.reduce((s, i) => s + i.qty, 0); // Assuming 1kg per unit
  const cartTotal = cartSubtotal + (shippingQuote?.shipping_cost || 0);

  // Calculate shipping when state changes
  const calculateShipping = async () => {
    if (!shippingState || cartItems.length === 0) {
      setShippingQuote(null);
      return;
    }
    setLoadingShipping(true);
    try {
      const res = await portalApi.post("/shipping/quote", {
        state: shippingState,
        postal_code: shippingPostal,
        total_weight_kg: totalWeight,
        order_subtotal: cartSubtotal,
      });
      setShippingQuote(res.data);
    } catch (err) {
      setShippingQuote({ shipping_cost: 0, message: "No se pudo calcular el envío" });
    } finally {
      setLoadingShipping(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shippingState && cartItems.length > 0) {
        calculateShipping();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [shippingState, shippingPostal, cartSubtotal]);

  const placeOrder = async (method) => {
    if (cartItems.length === 0) { toast.error("El carrito está vacío"); return; }
    if (!shippingState) { toast.error("Selecciona tu estado para el envío"); return; }
    if (!shippingAddress) { toast.error("Ingresa tu dirección de envío"); return; }
    
    setPlacing(true);
    try {
      const items = cartItems.map(i => ({
        product_id: i.product.id,
        product_name: i.product.name,
        quantity: i.qty,
        unit: i.product.unit,
        unit_price: i.product.portal_price,
        subtotal: i.product.portal_price * i.qty,
      }));
      const res = await portalApi.post("/orders/v2", {
        items,
        subtotal: cartSubtotal,
        shipping_cost: shippingQuote?.shipping_cost || 0,
        shipping_state: shippingState,
        shipping_address: shippingAddress,
        shipping_postal_code: shippingPostal,
        total: cartTotal,
        payment_method: method,
      });
      setCart({});
      setShippingQuote(null);
      setShippingState("");
      setShippingAddress("");
      setShippingPostal("");
      toast.success("¡Pedido realizado! Ahora procede al pago.");
      setTab("pedidos");
      load();
      // Navigate to order detail
      navigate(`/tienda/pedido/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al realizar el pedido");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-brand-600 text-white sticky top-0 z-40 shadow-md">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Package size={20} />
          <div className="flex-1">
            <div className="font-bold text-sm leading-tight">Rasputin Snacks</div>
            <div className="text-brand-200 text-xs truncate">Hola, {customer?.name}</div>
          </div>
          <button onClick={() => setTab("carrito")} className="relative p-2 bg-white/10 rounded-xl hover:bg-white/20">
            <ShoppingCart size={18} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 text-brand-900 text-xs font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
          <button onClick={() => { logout(); navigate("/tienda/login"); }} className="p-2 bg-white/10 rounded-xl hover:bg-white/20">
            <LogOut size={16} />
          </button>
        </div>
        {/* Tabs */}
        <div className="flex border-t border-brand-500 max-w-2xl mx-auto">
          {[["catalogo", "Catálogo"], ["carrito", `Carrito${cartCount > 0 ? ` (${cartCount})` : ""}`], ["pedidos", "Mis Pedidos"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${tab === k ? "border-b-2 border-amber-400 text-white" : "text-brand-200"}`}>
              {l}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* CATÁLOGO */}
        {tab === "catalogo" && (
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-12 text-slate-400">Cargando productos...</div>
            ) : products.length === 0 ? (
              <div className="text-center py-12 text-slate-400">No hay productos disponibles</div>
            ) : products.map(p => (
              <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                <div className="flex gap-3">
                  <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Package size={22} className="text-brand-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">{p.name}</div>
                    {p.description && <div className="text-xs text-slate-400 mt-0.5 truncate">{p.description}</div>}
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-brand-700 font-bold text-base">{formatMXN(p.portal_price)}</span>
                      <span className="text-xs text-slate-400">/ {p.unit}</span>
                      {p.stock > 0 ? (
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">En stock</span>
                      ) : (
                        <span className="text-xs bg-red-50 text-red-500 px-1.5 py-0.5 rounded-full">Sin stock</span>
                      )}
                    </div>
                  </div>
                </div>
                {p.stock > 0 && (
                  <div className="flex items-center gap-2 mt-3">
                    {qty(p.id) === 0 ? (
                      <button onClick={() => addItem(p)} data-testid={`add-product-${p.id}`}
                        className="flex-1 flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white py-2 rounded-xl text-sm font-medium transition-colors">
                        <Plus size={15} /> Agregar
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 flex-1">
                        <button onClick={() => removeItem(p)} className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl hover:bg-slate-50">
                          <Minus size={14} />
                        </button>
                        <input type="number" value={qty(p.id)} onChange={e => setQty(p, e.target.value)} min="1"
                          className="flex-1 text-center border border-brand-200 rounded-xl py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500" />
                        <button onClick={() => addItem(p)} className="w-9 h-9 flex items-center justify-center bg-brand-600 text-white rounded-xl hover:bg-brand-700">
                          <Plus size={14} />
                        </button>
                        <span className="text-xs text-slate-500 ml-1">{p.unit}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* CARRITO / CHECKOUT */}
        {tab === "carrito" && (
          <div className="space-y-3">
            {cartItems.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <ShoppingCart size={40} className="mx-auto mb-3 opacity-30" />
                <p>Tu carrito está vacío</p>
                <button onClick={() => setTab("catalogo")} className="mt-3 text-brand-600 font-medium text-sm">Ver catálogo →</button>
              </div>
            ) : (
              <>
                {/* Cart Items */}
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                  <h3 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
                    <ShoppingCart size={16} className="text-brand-600" /> Productos
                  </h3>
                  {cartItems.map(({ product: p, qty: q }) => (
                    <div key={p.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 text-sm">{p.name}</div>
                        <div className="text-xs text-slate-400">{formatMXN(p.portal_price)} / {p.unit}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => removeItem(p)} className="w-7 h-7 flex items-center justify-center border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50"><Minus size={12} /></button>
                        <span className="w-8 text-center text-sm font-semibold">{q}</span>
                        <button onClick={() => addItem(p)} className="w-7 h-7 flex items-center justify-center bg-brand-600 rounded-lg text-white hover:bg-brand-700"><Plus size={12} /></button>
                      </div>
                      <div className="text-sm font-bold text-brand-700 w-20 text-right">{formatMXN(p.portal_price * q)}</div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 border-t border-slate-200 mt-2">
                    <span className="text-sm text-slate-600">Subtotal productos</span>
                    <span className="font-bold text-slate-900">{formatMXN(cartSubtotal)}</span>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                  <h3 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
                    <Truck size={16} className="text-brand-600" /> Dirección de envío
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Estado *</label>
                      <select 
                        value={shippingState} 
                        onChange={e => setShippingState(e.target.value)}
                        data-testid="shipping-state-select"
                        className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                        <option value="">Selecciona tu estado</option>
                        {states.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Dirección completa *</label>
                      <textarea 
                        value={shippingAddress}
                        onChange={e => setShippingAddress(e.target.value)}
                        placeholder="Calle, número, colonia, municipio..."
                        data-testid="shipping-address-input"
                        className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Código Postal</label>
                      <input 
                        type="text"
                        value={shippingPostal}
                        onChange={e => setShippingPostal(e.target.value)}
                        placeholder="00000"
                        maxLength={5}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    </div>
                    
                    {/* Shipping Quote Result */}
                    {loadingShipping ? (
                      <div className="flex items-center justify-center py-3 text-slate-400">
                        <span className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin mr-2" />
                        Calculando envío...
                      </div>
                    ) : shippingQuote && (
                      <div className={`rounded-xl p-3 flex items-center justify-between ${shippingQuote.free_shipping ? "bg-emerald-50 border border-emerald-200" : "bg-brand-50 border border-brand-100"}`}>
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className={shippingQuote.free_shipping ? "text-emerald-600" : "text-brand-600"} />
                          <div>
                            <div className={`text-sm font-medium ${shippingQuote.free_shipping ? "text-emerald-700" : "text-brand-700"}`}>
                              {shippingQuote.zone_name}
                            </div>
                            <div className="text-xs text-slate-500">{shippingQuote.message}</div>
                          </div>
                        </div>
                        <div className={`text-lg font-bold ${shippingQuote.free_shipping ? "text-emerald-700" : "text-brand-700"}`}>
                          {shippingQuote.free_shipping ? "GRATIS" : formatMXN(shippingQuote.shipping_cost)}
                        </div>
                      </div>
                    )}
                    
                    {shippingQuote?.free_shipping_threshold && !shippingQuote.free_shipping && (
                      <div className="text-xs text-center text-amber-600 bg-amber-50 rounded-lg py-2 px-3">
                        💡 ¡Envío gratis en compras mayores a {formatMXN(shippingQuote.free_shipping_threshold)}!
                      </div>
                    )}
                  </div>
                </div>

                {/* Total & Payment */}
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Subtotal productos</span>
                      <span>{formatMXN(cartSubtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Envío</span>
                      <span>{shippingQuote ? (shippingQuote.free_shipping ? "GRATIS" : formatMXN(shippingQuote.shipping_cost)) : "-"}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <span className="font-bold text-slate-900">Total a pagar</span>
                      <span className="text-xl font-bold text-brand-700">{formatMXN(cartTotal)}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <button 
                      onClick={() => placeOrder("mercadopago")} 
                      disabled={placing || !shippingState || !shippingAddress}
                      data-testid="pay-mercadopago-btn"
                      className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      💳 Pagar con MercadoPago
                    </button>
                    <button 
                      onClick={() => placeOrder("transferencia")} 
                      disabled={placing || !shippingState || !shippingAddress}
                      data-testid="pay-transfer-btn"
                      className="w-full flex items-center justify-center gap-2 border border-brand-200 bg-brand-50 hover:bg-brand-100 text-brand-700 py-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      🏦 Transferencia bancaria
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 text-center mt-3">⏰ Tu pedido se reserva por 12 horas tras confirmar</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* MIS PEDIDOS */}
        {tab === "pedidos" && (
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-12 text-slate-400">Cargando...</div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 text-slate-400">Sin pedidos aún</div>
            ) : orders.map(o => (
              <OrderCard key={o.id} order={o} onNavigate={() => navigate(`/tienda/pedido/${o.id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order, onNavigate }) {
  const statusMap = {
    pendiente_pago: { label: "Esperando pago", color: "bg-amber-100 text-amber-700" },
    pago_enviado: { label: "Comprobante enviado", color: "bg-blue-100 text-blue-700" },
    pago_validado: { label: "Pago confirmado", color: "bg-emerald-100 text-emerald-700" },
    en_produccion: { label: "En preparación", color: "bg-brand-100 text-brand-700" },
    enviado: { label: "Enviado", color: "bg-emerald-100 text-emerald-700" },
    cancelado: { label: "Cancelado", color: "bg-red-100 text-red-600" },
  };
  const st = statusMap[order.portal_status] || { label: order.portal_status, color: "bg-slate-100 text-slate-600" };

  return (
    <button onClick={onNavigate} data-testid={`order-card-${order.id}`} className="w-full bg-white rounded-2xl border border-slate-200 p-4 text-left hover:border-brand-200 transition-colors shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-semibold text-slate-900 text-sm">{order.folio}</div>
          <div className="text-xs text-slate-400 mt-0.5">{new Date(order.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
          <ChevronRight size={14} className="text-slate-300" />
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-xs text-slate-500">{order.items?.length} producto{order.items?.length !== 1 ? "s" : ""}</div>
        <div className="font-bold text-brand-700">{formatMXN(order.total)}</div>
      </div>
      {order.portal_status === "pendiente_pago" && order.payment_deadline && (
        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2">
          <AlertCircle size={12} className="text-amber-500 flex-shrink-0" />
          <span className="text-xs text-amber-600">Tiempo para pagar: <CountdownBadge expiresAt={order.payment_deadline} /></span>
        </div>
      )}
    </button>
  );
}
